/**
 * Supabase Storage Utilities for LC Performance
 * 
 * Helper functions for uploading, deleting, and managing blog images
 * in Supabase Storage 'blog' bucket.
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

const BLOG_BUCKET = 'blog';

/**
 * Upload a blog image to Supabase Storage
 * @param {File} file - Image file to upload
 * @param {string} blogId - Blog ID (for organizing files)
 * @param {string} userId - User ID (for organizing files)
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export async function uploadBlogImage(file, blogId, userId) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured' };
  }

  if (!file) {
    return { success: false, error: 'No file provided' };
  }

  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    return { success: false, error: 'Invalid file type. Only JPEG, PNG, WebP, and GIF are allowed.' };
  }

  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return { success: false, error: 'File size exceeds 5MB limit' };
  }

  try {
    // Generate unique filename: blogId-timestamp-originalname
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileName = `${blogId || 'temp'}-${timestamp}.${fileExt}`;
    const filePath = userId ? `${userId}/${fileName}` : fileName;

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from(BLOG_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false, // Don't overwrite existing files
      });

    if (error) {
      console.error('Error uploading image:', error);
      return { success: false, error: error.message };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(BLOG_BUCKET)
      .getPublicUrl(data.path);

    return {
      success: true,
      url: urlData.publicUrl,
      path: data.path,
    };
  } catch (error) {
    console.error('Error uploading blog image:', error);
    return { success: false, error: error.message || 'Failed to upload image' };
  }
}

/**
 * Delete a blog image from Supabase Storage
 * @param {string} imagePath - Storage path of the image to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteBlogImage(imagePath) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured' };
  }

  if (!imagePath) {
    return { success: false, error: 'No image path provided' };
  }

  try {
    // Extract path from full URL if needed
    let path = imagePath;
    if (imagePath.includes(BLOG_BUCKET)) {
      // Extract path from URL like: https://...supabase.co/storage/v1/object/public/blog/path/to/image.jpg
      const parts = imagePath.split(`/${BLOG_BUCKET}/`);
      if (parts.length > 1) {
        path = parts[1];
      }
    }

    const { error } = await supabase.storage
      .from(BLOG_BUCKET)
      .remove([path]);

    if (error) {
      console.error('Error deleting image:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting blog image:', error);
    return { success: false, error: error.message || 'Failed to delete image' };
  }
}

/**
 * Get public URL for an image in Supabase Storage
 * @param {string} imagePath - Storage path of the image
 * @returns {string} Public URL
 */
export function getImageUrl(imagePath) {
  if (!imagePath || !isSupabaseConfigured) {
    return '';
  }

  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // Get public URL from Supabase Storage
  const { data } = supabase.storage
    .from(BLOG_BUCKET)
    .getPublicUrl(imagePath);

  return data.publicUrl;
}

/**
 * Resize image client-side before upload (optional optimization)
 * @param {File} file - Image file to resize
 * @param {number} maxWidth - Maximum width (default: 1920)
 * @param {number} maxHeight - Maximum height (default: 1080)
 * @param {number} quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<File>} Resized image file
 */
export async function resizeImage(file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to resize image'));
            }
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
